import { GameVariation, PlayerPieces, PieceType } from '../types';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { PieceGlyph } from './PieceGlyph';

interface PieceOption {
  key: PieceType;
  name: string;
  desc: string;
  gradient: string;
  icon: string;
}

interface PieceCustomizerProps {
  gameVariation: GameVariation;
  playerPieces: PlayerPieces;
  setPlayerPieces: React.Dispatch<React.SetStateAction<PlayerPieces>>;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

const pieceOptions: PieceOption[] = [
  { key: 'Q', name: 'Queen', desc: 'Bishop + Rook', gradient: 'from-cyan-500 to-pink-500', icon: '♕' },
  { key: 'H', name: 'Hawk', desc: 'Knight + Bishop', gradient: 'from-cyan-400 to-orange-500', icon: '♗*' },
  { key: 'E', name: 'Elephant', desc: 'Knight + Rook', gradient: 'from-green-500 to-emerald-500', icon: '♖*' },
  { key: 'A', name: 'Amazon', desc: 'Knight + Bishop + Rook', gradient: 'from-orange-500 to-red-500', icon: '♕*' }
];

const blackPieceOptions: PieceOption[] = [
  { key: 'Q', name: 'Queen', desc: 'Bishop + Rook', gradient: 'from-cyan-500 to-pink-500', icon: '♛' },
  { key: 'H', name: 'Hawk', desc: 'Knight + Bishop', gradient: 'from-cyan-400 to-orange-500', icon: '♝*' },
  { key: 'E', name: 'Elephant', desc: 'Knight + Rook', gradient: 'from-green-500 to-emerald-500', icon: '♜*' },
  { key: 'A', name: 'Amazon', desc: 'Knight + Bishop + Rook', gradient: 'from-orange-500 to-red-500', icon: '♛*' }
];

export const PieceCustomizer = ({
  gameVariation,
  playerPieces,
  setPlayerPieces,
  isExpanded = false,
  onToggleExpand
}: PieceCustomizerProps) => {
  const isStandardOr960 = ['chess', 'chessx2', 'chess960', 'chess960x2'].includes(gameVariation);
  // Blind Chess960 shares ONE major piece between both players (like x8).
  const isX8Variant = gameVariation.includes('x8') || gameVariation === 'chess960blind';
  const isX32Variant = gameVariation.includes('x32');

  // If standard or chess960 variants, don't show piece customization
  if (isStandardOr960) {
    return (
      <Card variant="glass" padding="md">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">Piece Customization</h3>
          <Badge variant="outline">Auto-selected</Badge>
        </div>
        <p className="text-sm text-slate-300 mt-2">
          In {gameVariation.includes('chess960') ? 'Chess960' : 'Standard Chess'}, both players use standard Queens.
        </p>
        <div className="mt-4 flex items-center justify-center">
          <div className="bg-gradient-to-r from-cyan-500 to-pink-500 rounded-lg p-4">
            <span className="text-3xl">
              <PieceGlyph piece="Q" color="white" />
            </span>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card variant="glass" padding="md">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-white">Piece Customization</h3>
        {onToggleExpand && (
          <button
            onClick={onToggleExpand}
            className="text-sm text-slate-400 hover:text-white transition-colors"
          >
            {isExpanded ? 'Hide' : 'Show'} Options
          </button>
        )}
      </div>

      {isExpanded && (
        <div className="space-y-6 animate-fade-in">
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-white">
                {isX8Variant ? 'Choose Major Piece for Both Players' : 'Choose Major Piece for White'}
              </h4>
              <Badge variant={playerPieces.white ? 'primary' : 'warning'}>
                {playerPieces.white ? 'Selected' : 'Not selected'}
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {pieceOptions.map(piece => (
                <button
                  key={piece.key}
                  onClick={() => {
                    if (isX8Variant) {
                      setPlayerPieces({ white: piece.key, black: piece.key });
                    } else {
                      setPlayerPieces(prev => ({ ...prev, white: piece.key }));
                    }
                  }}
                  className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                    (isX8Variant
                      ? (playerPieces.white === piece.key && playerPieces.black === piece.key)
                      : playerPieces.white === piece.key)
                      ? 'border-cyan-500 bg-cyan-500/20'
                      : 'border-slate-700 bg-slate-800/50 hover:bg-slate-800'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`bg-gradient-to-r ${piece.gradient} rounded-lg p-2 mr-3 w-10 h-10 flex items-center justify-center`}>
                      <span className="text-white text-xl">
                        <PieceGlyph piece={piece.key} color="white" />
                      </span>
                    </div>
                    <div className="text-left">
                      <div className="font-medium text-white text-sm">{piece.name}</div>
                      <div className="text-xs text-slate-300">{piece.desc}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {isX32Variant && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-white">Choose Major Piece for Black</h4>
                <Badge variant={playerPieces.black ? 'primary' : 'warning'}>
                  {playerPieces.black ? 'Selected' : 'Not selected'}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {blackPieceOptions.map(piece => (
                  <button
                    key={piece.key}
                    onClick={() => setPlayerPieces(prev => ({ ...prev, black: piece.key }))}
                    className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                      playerPieces.black === piece.key
                        ? 'border-cyan-500 bg-cyan-500/20'
                        : 'border-slate-700 bg-slate-800/50 hover:bg-slate-800'
                    }`}
                  >
                    <div className="flex items-center">
                      <div className={`bg-gradient-to-r ${piece.gradient} rounded-lg p-2 mr-3 w-10 h-10 flex items-center justify-center`}>
                        <span className="text-white text-xl">
                          <PieceGlyph piece={piece.key} color="black" />
                        </span>
                      </div>
                      <div className="text-left">
                        <div className="font-medium text-white text-sm">{piece.name}</div>
                        <div className="text-xs text-slate-300">{piece.desc}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {!isX8Variant && !isX32Variant && (
            <div className="bg-gradient-to-r from-cyan-500/20 to-yellow-500/20 rounded-lg p-4 border border-cyan-500/30">
              <p className="text-sm text-cyan-200">
                <strong>Note:</strong> In {gameVariation}, both players use the same major piece.
                When you select a piece from above, it will automatically be applied to both White and Black players.
              </p>
            </div>
          )}
        </div>
      )}

      {!isExpanded && (
        <div className="text-sm text-slate-300">
          <p>Customize pieces for advanced game variants (x8, x32).</p>
          {onToggleExpand && (
            <button
              onClick={onToggleExpand}
              className="mt-2 text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              Click to expand options
            </button>
          )}
        </div>
      )}
    </Card>
  );
};